import { TwinEmployeeParametersDict } from '@teinor/erp/types/company/userInner/employeeParameter'
import { Fragment, useCallback, useEffect, useState } from 'react'
import { ComponentWithPermissions } from '../../../../../../../baseComponents/CreateRoutering/types'
import SingleFormLayout from '../../../../../../../baseComponents/Layout/SingleFormLayout'
import { OnSubmit } from '../../../../../../../forms/TwinForm/types'
import { FormRender } from '../../../../../../../forms/FormRenderer/types'
import { valueOrDefaultValue } from '../../../../../../../utils/globals/valueOrDefaultValue'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { changeEmployeeParams } from '../../../../../../../utils/reducers/reduxDispatch'
import { TwinDictionary } from '../../../../../../../utils/globals/dictionary'
import { twinFetchPostJSON } from '../../../../../../../utils/globals/data'
import LoadingSpinner from '../../../../../../../baseComponents/LoaderDecider/LoadingSpinner'
import useEditDeleteModal from '../../../../../../../utils/hooks/useEditDeleteModal'
import { ConfigEmployeeEmailCustomRender, ModalDeleteEmployeeEmailConfigQuote, ModalEmployeeEmailParametersConfig } from './EmployeeEmailConfigComponents'
import { ConfigEmployeeMultiUserPassCustomRender, ModalEmployeeMultiUserPass } from './EmployeeMultiUserPass'
import { EmployeeModelType } from '@teinor/erp/types/company/userInner/employee'
import { ModalOpenedTy } from '../../../../../../../baseComponents/Modal/types'

type EmployeeParametersProps = EmployeeParametersLogicParamsProps & ComponentWithPermissions & {
    basicData: EmployeeModelType | null
}

const EmployeeParameters: React.FC<EmployeeParametersProps> = ({ getData, userPermissions, basicData }) => {
    const { onSubmit, employeeParameters, opened, setOpened } = useEmployeeParameters({ getData })
    const { openModal, setOpenModal} = useEditDeleteModal()
    const { t } = useTwinTranslation()
    const onOff = {
        'on': { name: t('on', 'Activado') },
        'off': { name: t('disabledS', 'Desactivado') },
    }
    const appStyle = {
        'default': { name: t('default', 'Por defecto') },
        'new1': { name: t('new', 'Nuevo') },
    }
    const taskAiType = {
        voiceToText: { name: t('voiceToText', 'Voz a texto')},
        voiceToResume: { name: t('createResume', 'Crear resumen')},
        voiceToTaskResume: { name: t('createResumeAndTask', 'Crear resumen y tareas')},
    }
    if(!employeeParameters){
        return <LoadingSpinner />
    }
    const fields: FormRender<TwinEmployeeParametersDict> = [
        {
            cols: 3,
            title: t('parameters', 'Parámetros'),
            elements: [
                {
                    name: 'dark_mode',
                    label: t('darkMode', 'Modo oscuro'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'appStyle',
                    label: t('appStyle', 'Estilo de la app'),
                    component: 'CustomSelect',
                    items: appStyle
                },
                {
                    name: 'mailHolidayNotification',
                    label: t('mailHolidayNotification', 'Notificaciones de solicitud de vacaciones'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'floatingTasks',
                    label: t('floatingTasks', 'Tareas en ventanas flotantes'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'showBubbleInnerTabs',
                    label: t('showBubbleInnerTabs', 'Menú de opciones en ventana flotante'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'autoSubscribeWhenTaskAssigned',
                    label: t('autoSubscribeWhenTaskAssigned', 'Suscribirse cuando te asignan una tarea'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'autoUnsubscribeWhenTaskUnassigned',
                    label: t('autoUnsubscribeWhenTaskUnassigned', 'Darse de baja cuando te desasignan una tarea'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'receiveNotificationFromTaskGroups',
                    label: t('receiveNotificationFromTaskGroups', 'Recibir notificaciones de las tareas de mi grupo'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'receiveMailFromCustomerProjects',
                    label: t('receiveMailFromCustomerProjects', 'Recibir email de los proyectos de clientes'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'saveTableDefaultFilterWhenChanges',
                    label: t('saveDefaultFilterWhenChanges', 'Guardar filtro por defecto al cambiar'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'saveTaskTableDefaultFilterWhenChanges',
                    label: t('saveTaskDefaultFilterWhenChanges', 'Guardar filtro por defecto al cambiar en las Tareas'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'closeMenuByDefault',
                    label: t('closeMenuByDefault', 'Menús cerrados por defecto'),
                    component: 'CustomSelect',
                    items: onOff
                },
                {
                    name: 'taskAiType',
                    label: t('taskAiType', 'Tipo de IA en tareas'),
                    component: 'CustomSelect',
                    items: taskAiType
                },
            ]
        },
        {
            cols: 1,
            title: t('contactEmailParameters', 'Parámetros de email de contacto'),
            elements: [
                {
                    name: 'textForRef',
                    component: 'Custom',
                    render: ({extraProps}) => <ConfigEmployeeEmailCustomRender setOpenModal={setOpenModal} employeeParameters={extraProps?.employeeParameters} />,
                    extraProps: {
                        employeeParameters
                    }
                },
            ]
        },
        {
            cols: 1,
            title: t('accessFromOtherUsers', 'Acceso desde otro usuario'),
            elements: [
                {
                    name: 'textForRef',
                    component: 'Custom',
                    render: ({ extraProps }) => <ConfigEmployeeMultiUserPassCustomRender setOpened={setOpened} />,
                },
            ]
        },
    ]
    const parsedFields = valueOrDefaultValue(fields, userPermissions, employeeParameters)
    return (
        <Fragment>
            <SingleFormLayout action='/api/app/config/employee/updateEmployeeParameters' onSubmit={onSubmit} sections={parsedFields} userPermissions={userPermissions} />
            {openModal?.type === 'create' ? <ModalEmployeeEmailParametersConfig onSubmit={onSubmit} setOpened={() => setOpenModal(null)} /> : null}
            {openModal?.type === 'edit' ? <ModalEmployeeEmailParametersConfig onSubmit={onSubmit} setOpened={() => setOpenModal(null)} allRowData={employeeParameters} userPermissions={userPermissions} /> : null}
            {openModal?.type === 'delete' ? <ModalDeleteEmployeeEmailConfigQuote onSubmit={onSubmit} setOpened={() => setOpenModal(null)}/> : null}
            {opened ? <ModalEmployeeMultiUserPass onSubmit={onSubmit} setOpened={setOpened} employeeData={basicData} url='/api/app/employee/updateMyEmployee'/> : null}
        </Fragment>
    )
}

interface EmployeeParametersLogicParamsProps {
    getData: () => Promise<void>
}

const useEmployeeParameters = ({ getData }: EmployeeParametersLogicParamsProps) => {
    const [employeeParameters, setEmployeeParameters] = useState<TwinDictionary | null>(null)
    const [opened, setOpened] = useState<ModalOpenedTy>(null)

    const getEmployeeParameters = useCallback(async() => {
        const myData = await twinFetchPostJSON('/api/user/getUserInstance', {})
        if(myData){
            setEmployeeParameters(myData?.Employee?.EmployeeParameters)        
        }
    }, [setEmployeeParameters])


    const onSubmit: OnSubmit = useCallback((_res, values) => {
        getData()
        changeEmployeeParams(values)
        getEmployeeParameters()
    }, [getData, getEmployeeParameters])

    useEffect(() => {
        getEmployeeParameters()
    }, [getEmployeeParameters])

    return { onSubmit, employeeParameters, opened, setOpened }
}


export default EmployeeParameters